import { SearchParams, TextTag, Range } from './types';
import { FIELDS } from './fields';
import { escapeHtml, readFormField } from './dom-helpers';
import { MATERIAL_FORMATS, materialIdAttr, $materialPanel } from './materials';
import { getAudienceParams } from './audience-params-store';
import { getAssignedUsers, getAssignedCompanies } from './assign_user';

const CAMPAIGN_INFO_FIELDS_TO_REVIEW: Array<{ key: string; label: string }> = [
  { key: 'name',         label: 'Campaign Name' },
  { key: 'primary_text', label: 'Primary Text' },
  { key: 'headline',     label: 'Headline' },
  { key: 'objective',    label: 'Objective' },
  { key: 'budget_amount', label: 'Budget' },
  { key: 'special_ad_categories', label: 'Ad Categories' },
];

function renderSearchParamRows(p: Partial<SearchParams>): string[] {
  const rows: string[] = [];
  FIELDS.forEach(({ key, label, kind }) => {
    const value = (p as Record<string, unknown>)[key];
    let tags: string[] = [];

    switch (kind) {
      case 'text-tags': {
        const arr = (value as TextTag[] | undefined) ?? [];
        tags = arr.map(t => t?.text).filter(Boolean) as string[];
        break;
      }
      case 'string-array':
        tags = ((value as string[] | undefined) ?? []).filter(Boolean);
        break;
      case 'keywords': {
        const k = ((value as string | undefined) ?? '').trim();
        if (k) tags = [k];
        break;
      }
      case 'range': {
        const range = value as Range | undefined;
        if (range && (range.min != null || range.max != null)) {
          const min = range.min ?? '';
          const max = range.max ?? '';
          tags = [`${min}${min !== '' || max !== '' ? ' – ' : ''}${max}`.trim()];
        }
        break;
      }
    }

    if (tags.length === 0) return;
    const tagHtml = tags
      .map(t => `<span class="badge badge-primary-light">${escapeHtml(t)}</span>`)
      .join(' ');
    rows.push(
      `<div class="d-flex flex-wrap align-items-center gap-2">
         <span class="text-muted small">${escapeHtml(label)}:</span>
         ${tagHtml}
       </div>`
    );
  });
  return rows;
}

function formatCampaignInfoValue($form: JQuery, key: string, value: string): string {
  if (!value) return '';
  if (key === 'budget_amount') {
    const n = Number(value);
    const amount = Number.isFinite(n) ? n.toLocaleString('en-US') : value;
    const type = readFormField($form, 'budget_type');
    return type ? `${type} - ${amount} SEK` : `${amount} SEK`;
  }
  if (key === 'start_date' || key === 'end_date') {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return value;
}

export function populateReview(): void {
  const $body = $('#ad-campaign-modal-body');

  const audienceName = $body.attr('data-audience-name') ?? '';
  const $audience = $('[data-role="summary-audience"]').empty();
  if (!audienceName) {
    $audience.html('<span class="text-muted">No audience selected</span>');
  } else {
    const audienceParams = getAudienceParams();

    const rows = renderSearchParamRows(audienceParams);
    if (rows.length === 0) {
      $audience.append('<span class="text-muted small">No search filters</span>');
    } else {
      const $list = $('<div class="d-flex flex-column gap-2"></div>');
      $list.append(`<div class="d-flex flex-wrap align-items-center gap-2">
         <span class="text-muted small">Name:</span>
         <span class="fw-bold">${escapeHtml(audienceName)}</span>
       </div>`);
      rows.forEach(row => $list.append(row));
      $audience.append($list);
    }
  }

  const $materials = $('[data-role="summary-materials"]').empty();
  MATERIAL_FORMATS.forEach(fmt => {
    const id = $body.attr(materialIdAttr(fmt));
    const $panel = $materialPanel(fmt);
    const targetWidth = $panel.data('material-width');
    const targetHeight = $panel.data('material-height');
    const labelText = `${fmt === 'square' ? 'Square' : 'Story'} (${targetWidth} x ${targetHeight})`;

    if (!id) {
      $materials.append(
        `<div class="summary-material">
           <span class="summary-material-label text-secondary">${escapeHtml(labelText)}</span>
           <div class="summary-material-thumb"></div>
           <span class="summary-material-name text-muted">No material selected</span>
         </div>`
      );
      return;
    }

    const $card = $panel.find(`.image-card[data-material-id="${id}"]`);
    const name = $card.find('.card-title').text() || `Material #${id}`;
    const isVideo = $card.find('video').length > 0;
    const src = (isVideo ? $card.find('video').attr('src') : $card.find('img').attr('src')) ?? '';

    let thumbContent = '';
    if (src && isVideo) {
      thumbContent = `<video src="${escapeHtml(src)}" muted preload="metadata"></video>`;
    } else if (src) {
      thumbContent = `<img src="${escapeHtml(src)}" alt="${escapeHtml(name)}">`;
    }

    $materials.append(
      `<div class="summary-material mb-2">
         <span class="summary-material-label text-secondary">${escapeHtml(labelText)}</span>
         <div class="bg-white d-flex align-items-center summary-material-thumb-wrapper">
          <div class="summary-material-thumb mr-3">${thumbContent}</div>
          <span class="summary-material-name font-weight-bold">${escapeHtml(name)}</span>
         </div>
       </div>`
    );
  });

  const $info = $('[data-role="summary-campaign-info"]').empty();
  const $form = $('#ad-campaign-info-step form.campaign-form');
  CAMPAIGN_INFO_FIELDS_TO_REVIEW.forEach(({ key, label }) => {
    const $field = $form.find(`[name="${key}"], [name$="[${key}]"]`).first();
    const raw = ($field.val() ?? '').toString().trim();
    const formatted = formatCampaignInfoValue($form, key, raw);
    const displayValue = formatted ? escapeHtml(formatted) : '<span class="text-muted">—</span>';
    $info.append(
      `<div class="col-md-6 mb-3">
         <h6 class="mb-1">${escapeHtml(label)}</h6>
         <div class="font-weight-bold campaign-info-value">${displayValue}</div>
       </div>`
    );
  });

  const geoLabelsFor = (role: string): string[] =>
    $form
      .find(`[data-role="${role}"] input[type="checkbox"]:checked`)
      .map((_i, el) => $(el).closest('.form-check').find('.form-check-label').text().trim())
      .get()
      .filter(Boolean);

  const appendGeoInfo = (label: string, labels: string[]): void => {
    const display = labels.length
      ? escapeHtml(labels.join(', '))
      : '<span class="text-muted">—</span>';
    $info.append(
      `<div class="col-md-6 mb-3">
         <h6 class="mb-1">${escapeHtml(label)}</h6>
         <div class="font-weight-bold campaign-info-value">${display}</div>
       </div>`
    );
  };

  appendGeoInfo('Special Ad Category Country', geoLabelsFor('campaign-geo-location'));
  appendGeoInfo('Ad set Geographic locations', geoLabelsFor('campaign-adset-geo-location'));

  const $assignment = $('[data-role="summary-assignment"]').empty();
  const assignedCompanies = getAssignedCompanies();
  const assignedUsers = getAssignedUsers();
  if (!assignedCompanies.length && !assignedUsers.length) {
    $assignment.html('<span class="text-muted">Not assigned</span>');
  } else {
    const $list = $('<div class="d-flex flex-column gap-2"></div>');
    const appendRow = (label: string, names: string[]) => {
      const tags = names
        .map(n => `<span class="badge badge-primary-light">${escapeHtml(n)}</span>`)
        .join(' ');
      $list.append(`<div class="d-flex flex-wrap align-items-center gap-2">
         <span class="text-muted small">${label}:</span>
         ${tags}
       </div>`);
    };
    if (assignedCompanies.length) appendRow('Companies', assignedCompanies.map(c => c.name));
    if (assignedUsers.length) appendRow('Users', assignedUsers.map(u => u.name));
    $assignment.append($list);
  }
}
